import { useState } from "react";
import { Check, CaretDown, MagnifyingGlass } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";

/**
 * Searchable font picker.
 * Props:
 *   value: current font name
 *   options: list of font names
 *   onChange: (name) => void
 *   placeholder, testId
 */
export default function FontPicker({ value, options, onChange, placeholder = "Search fonts…", testId }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className="mt-2 w-full h-10 px-3 rounded-sm border border-border bg-card text-left text-sm flex items-center justify-between hover:bg-muted/40 transition-colors"
          style={{ fontFamily: value }}
        >
          <span className="truncate">{value || "Choose a font…"}</span>
          <CaretDown size={14} className="text-muted-foreground shrink-0 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-[--radix-popover-trigger-width] max-w-md">
        <Command>
          <div className="flex items-center border-b border-border px-3" cmdk-input-wrapper="">
            <MagnifyingGlass size={14} className="text-muted-foreground shrink-0" />
            <CommandInput
              placeholder={placeholder}
              data-testid={`${testId}-search`}
              className="border-0 focus:ring-0 h-10 text-sm"
            />
          </div>
          <CommandList className="max-h-72">
            <CommandEmpty>No font found.</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt}
                  value={opt}
                  onSelect={(v) => {
                    // Command lower-cases the value; match against options
                    const match = options.find((o) => o.toLowerCase() === v.toLowerCase()) || v;
                    onChange(match);
                    setOpen(false);
                  }}
                  className="flex items-center justify-between cursor-pointer"
                  data-testid={`font-option-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <span className="truncate" style={{ fontFamily: opt }}>{opt}</span>
                  <Check
                    size={14}
                    weight="bold"
                    className={cn("ml-2 shrink-0", value === opt ? "opacity-100" : "opacity-0")}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
